import { useMemo } from 'react';
import { useEnterpriseScope } from './useEnterpriseScope';
import { useFlooScopeOptions, type FlooScopeType } from './useFlooScopeOptions';

/**
 * Floo Network Phase 8 — Active Scope Context adapter.
 *
 * Bridges the Phase 6 client-side active view scope to the shape every
 * existing scope-aware hook/RPC expects (`scopeType`/`scopeId`/label),
 * without requiring those hooks to know about Phase 6. The contract:
 *
 *   • When the user has actively selected a Floo Network scope (via
 *     the ScopeSwitcher), the adapter returns that scope verbatim.
 *   • Otherwise, the adapter falls back to the membership-derived
 *     `useEnterpriseScope` values — preserving every page's prior
 *     behavior for users who never interact with the switcher.
 *   • `isActiveScopeReady` distinguishes "user actively chose a
 *     scope" from "falling back to membership default", so pages can
 *     decide whether to surface the scope indicator.
 *
 * Read-only by construction:
 *   • No mutations. No localStorage writes.
 *   • Does not call `setActiveScope`.
 *   • Does not reimplement Phase 6 permission logic — defers to
 *     `useFlooScopeOptions` for boundary validation.
 *
 * Phase 8 stays narrow: this adapter exists to thread one piece of
 * state through the existing dashboards. It does NOT introduce new
 * RPCs, new edge functions, or new permission checks.
 */

export interface FlooActiveScopeContext {
  scopeType: FlooScopeType | null;
  scopeId: string | null;
  /** Display name of the active scope target ("Midwest Region"). */
  label: string;
  /** Capitalized level name ("Region"). */
  levelLabel: string;
  /**
   * `true` when the user has actively chosen a scope through the
   * Phase 6 switcher. `false` when we're falling back to the
   * membership-derived default (no user choice yet).
   */
  isActiveScopeReady: boolean;
}

const LEVEL_LABEL: Record<string, string> = {
  organization: 'Organization',
  region: 'Region',
  district: 'District',
  shop: 'Shop',
};

/**
 * Returns the active view scope when set, otherwise the membership
 * scope as a fallback. Both shapes are normalized to a single
 * `FlooActiveScopeContext` so consumers don't have to branch.
 */
export function useFlooActiveScopeContext(): FlooActiveScopeContext {
  const { activeScope } = useFlooScopeOptions();
  const enterpriseScope = useEnterpriseScope();

  return useMemo<FlooActiveScopeContext>(() => {
    if (activeScope) {
      return {
        scopeType: activeScope.type,
        scopeId: activeScope.id,
        label: activeScope.label,
        levelLabel: activeScope.levelLabel,
        isActiveScopeReady: true,
      };
    }
    // Fallback to membership-derived scope. `useEnterpriseScope`
    // returns scopeType as a string when set; we narrow to the
    // FlooScopeType union here for downstream consumers.
    const fallbackType = (enterpriseScope.scopeType ?? null) as FlooScopeType | null;
    return {
      scopeType: fallbackType,
      scopeId: enterpriseScope.scopeId ?? null,
      label: enterpriseScope.scopeLabel,
      levelLabel: fallbackType ? LEVEL_LABEL[fallbackType] ?? enterpriseScope.scopeLabel : enterpriseScope.scopeLabel,
      isActiveScopeReady: false,
    };
  }, [
    activeScope,
    enterpriseScope.scopeType,
    enterpriseScope.scopeId,
    enterpriseScope.scopeLabel,
  ]);
}
